import Markdown from 'markdown-to-jsx';
import model from '../../resources/model/model';
import { ChordBox } from '../chordBox';
import './lyrics.scss';

//bare numbers are em, anything else is taken as a css length
const cssLength = value => /^-?[\d.]+$/.test(value) ? value + "em" : value

const replacers = [
  {
    reg: /\\h(?:skip)?{(.*?)}/g,
    fun: (str, width) => `<span class="hskip" style="width:${cssLength(width)}"> </span>`
  },
  {
    reg: "|:",
    fun: x => "𝄆"
  },
  {
    reg: ":|",
    fun: x => "𝄇"
  },
  //whole line sung by one voice: "\v2 text" or "\v2: text"
  {
    reg: /^\\v([1-6])[ :]\s*(.*)$/gm,
    fun: (str, voice, line) => `<span class="voice v${voice}">${line}</span>`
  },
  //part of a line sung by one voice: "\v1{text}"
  //the spaces around it become tags of their own, because markdown drops whitespace
  //that would sit between two tags - as in "\v1{The} [Em]snow" - and moving one
  //inside the span does not help either: a span whose text starts with a chord is
  //parsed as a block, which trims the space right back off
  {
    reg: /([ \t]*)\\v([1-6]){(.*?)}([ \t]*)/g,
    fun: (str, before, voice, part, after) =>
      `${before ? "<wsp></wsp>" : ""}<span class="voice v${voice}">${part}</span>${after ? "<wsp></wsp>" : ""}`
  },
  //chords, optionally scaled down so that several of them fit above one word
  //without overwriting each other: [G#dim|.7], [A#|.6] or [Dm Am A|.8]
  {
    reg: /\[([^[\]\r\n]*?)(?:\|([^[\]|\r\n]*))?\]/g,
    fun: (str, chords, scale) => {
      if (!model.settings.displayChords) return ""
      //clamped, so that a mistyped scale ([G|7] instead of [G|.7]) cannot wreck the line
      const size = Math.min(parseFloat(scale), 3)
      return `<chord${size > 0 ? ` scale="${Math.max(size, .2)}"` : ""}>${chords}</chord>`
    }
  },
  {
    reg: /[Jj]ít/g,
    fun: str => model.state.settings.yeet ? (str[0] === "J" ? "Yeet" : "yeet") : str
  },
]

//extra space below a line: \vspace{.5em}, or \vspace{-.3} to tighten it up
const vspace = /\\v(?:space|skip){(.*?)}/g

//whitespace, including the invisible characters songs used as spacing tricks
const blank = /[\s\u200b\u200e\ufeff\u00a0]/g

//older songs padded chord only lines with \h{5em} or invisible characters to
//force a line height - the layout gives them one now, the padding would only
//widen the line
const trailingPadding = /(?:<span class="hskip"[^>]*>[^<]*<\/span>|[\s\u200b\u200e\ufeff\u00a0])+$/

const hasChords = row => row.includes("<chord")

//lines that carry nothing but chords (intermezzos) are laid out in the normal
//flow instead of floating above the lyrics, so that they cannot collapse onto
//each other
function isChordOnly(row) {
  if (!hasChords(row)) return false
  return !row
    .replaceAll(/<chord[^>]*>.*?<\/chord>/g, "")
    .replaceAll(/<[^>]*>/g, "")
    .replaceAll(blank, "")
    .length
}

export function Lyrics({ children = "", name = "" }) {
  let text = (typeof children === "string") ? children : ""
  if (typeof name !== "string") name = ""

  replacers.forEach(r => text = text.replaceAll(r.reg, r.fun))

  const rows = text.split("\n")

  return <div className="chord-lyrics">

    {rows.map((row, key) => {
      let space = ""
      let content = row.replaceAll(vspace, (str, value) => { space = cssLength(value); return "" })

      const chordOnly = isChordOnly(content)
      if (chordOnly) content = content.replace(trailingPadding, "")

      return <div
        key={key}
        className={`lyrics-line ${hasChords(content) ? "has-chords" : ""} ${chordOnly ? "chord-only" : ""}`}
        style={space ? { marginBottom: space } : undefined}
      >
        {key === 0 && <div className="verse-name">{name}</div>}
        <Markdown
          options={{
            overrides: {
              chord: {
                component: ChordBox
              },
              //a chord opening a voice span makes markdown wrap the rest of it in
              //a paragraph, which would break the line apart - keep it inline
              p: {
                component: "span"
              },
              //the space that followed a voice span, see the replacer
              wsp: {
                component: () => " "
              },
            }
          }}
        >{content}</Markdown>
      </div>
    })}
  </div>
}
